/**
 * Top-level App component.
 *
 * Renders one of:
 *   - <Login /> if no MSAL account is signed in
 *   - <PendingApproval /> if signed in but backend says status=pending
 *   - <Shell /> for active users (sidebar nav + content area, role-driven)
 *
 * Keeps routing minimal for MVP — switching on a top-level "screen" state. We
 * upgrade to TanStack Router once we have >5 routes.
 */

import { useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Login } from "./screens/Login";
import { Shell } from "./screens/Shell";
import { PendingApproval } from "./screens/PendingApproval";
import { api, ApiError } from "./lib/api";
import type { AppRole } from "./lib/roles";

type Me = {
  id: string; email: string; displayName?: string; role: AppRole; tenantId: string;
  capabilities?: { pbxEnabled: boolean; acdEnabled: boolean };
  directDialNumber?: string | null;
};

export function App() {
  const isAuth = useIsAuthenticated();
  const { instance } = useMsal();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuth) {
      setMe(null);
      return;
    }
    setLoading(true);
    api<Me>("/api/me")
      .then((u) => { setMe(u); setError(null); })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) {
          setError("pending");
        } else {
          setError((e as Error).message);
        }
        setMe(null);
      })
      .finally(() => setLoading(false));
  }, [isAuth]);

  if (!isAuth) return <Login />;
  if (loading) return <div className="grid place-items-center h-full text-rogue-700">Loading…</div>;
  if (error === "pending") return <PendingApproval onSignOut={() => instance.logoutRedirect()} />;
  if (error) {
    return (
      <div className="grid place-items-center h-full text-red-700">
        <div className="text-center pad-fluid">
          <p>Authentication error: {error}</p>
          <button className="mt-4 underline" onClick={() => instance.logoutRedirect()}>
            Sign out and try again
          </button>
        </div>
      </div>
    );
  }
  if (!me) return null;
  return <Shell me={me} onSignOut={() => instance.logoutRedirect()} />;
}
